import { isPdlMissingValue } from '@web-studio/shared-schemas';
import { EditableContent } from '../../edit/EditableContent';
import { EditableSectionHeading } from '../../edit/EditableSectionHeading';
import { pickBlockField } from '../../lib/localized';
import { pickChrome } from '../../lib/siteChromeCopy';
import { parseHoursSchedule } from '../../lib/splitHoursLines';
import type { BlockRenderProps } from '../../types';
import { FooterLegalLinks } from './FooterLegalLinks';
import { FooterSocialLinks } from './FooterSocialLinks';

function pickFooterContactField(
  content: BlockRenderProps['block']['content'],
  key: string,
  locale: string,
): string {
  const value = pickBlockField(content, key, locale);
  return isPdlMissingValue(value) ? '' : value;
}

export function FooterStandard({
  block,
  locale,
  isActive,
  activeEditTarget,
  editable,
  pages,
  defaultLocale = locale,
  onNavigateToPageSlug,
}: BlockRenderProps) {
  const siteTitle = pickBlockField(block.content, 'siteTitle', locale);
  const address = pickFooterContactField(block.content, 'address', locale);
  const phone = pickFooterContactField(block.content, 'phone', locale);
  const email = pickFooterContactField(block.content, 'email', locale);
  const hours = pickFooterContactField(block.content, 'hours', locale);
  const hoursRows = parseHoursSchedule(hours);
  const legalLine = pickBlockField(block.content, 'legalLine', locale);
  const target = isActive ? activeEditTarget : null;

  return (
    <footer
      className={['ws-footer', isActive ? 'ws-block--active' : ''].filter(Boolean).join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
      data-footer-arrangement="standard"
      id="contact"
    >
      <div className="ws-footer__inner">
        <div className="ws-footer__group ws-footer__group--brand">
          <EditableContent
            as="p"
            fieldKey="siteTitle"
            editable={editable}
            activeEditTarget={target}
            className="ws-footer__brand"
          >
            {siteTitle}
          </EditableContent>
          <EditableContent
            as="p"
            fieldKey="address"
            editable={editable}
            activeEditTarget={target}
            className="ws-footer__text"
          >
            {address}
          </EditableContent>
          <FooterSocialLinks
            content={block.content}
            locale={locale}
            editable={editable}
            activeEditTarget={target}
          />
        </div>
        <div className="ws-footer__group ws-footer__group--contact">
          <EditableSectionHeading
            label={pickChrome('footerContact', locale)}
            fieldKey="phone"
            editable={editable}
            className="ws-footer__heading"
          />
          <EditableContent
            as="p"
            fieldKey="phone"
            editable={editable}
            activeEditTarget={target}
            className="ws-footer__text"
          >
            {phone ? <a href={`tel:${phone.replace(/\s/g, '')}`}>{phone}</a> : null}
          </EditableContent>
          <EditableContent
            as="p"
            fieldKey="email"
            editable={editable}
            activeEditTarget={target}
            className="ws-footer__text"
          >
            {email ? <a href={`mailto:${email}`}>{email}</a> : null}
          </EditableContent>
        </div>
        <div className="ws-footer__group ws-footer__group--hours">
          <EditableSectionHeading
            label={pickChrome('footerHours', locale)}
            fieldKey="hours"
            editable={editable}
            className="ws-footer__heading"
          />
          {hoursRows.length > 0 ? (
            <EditableContent
              as="div"
              fieldKey="hours"
              editable={editable}
              activeEditTarget={target}
              className="ws-hours-schedule"
            >
              {hoursRows.map((row, index) => (
                <div key={index} className="ws-hours-schedule__row">
                  <span className="ws-hours-schedule__day">{row.day}</span>
                  <span className="ws-hours-schedule__time">{row.time}</span>
                </div>
              ))}
            </EditableContent>
          ) : null}
        </div>
        <div className="ws-footer__legal">
          <FooterLegalLinks
            pages={pages}
            locale={locale}
            defaultLocale={defaultLocale}
            editable={editable}
            activeEditTarget={target}
            content={block.content}
            onNavigateToPageSlug={onNavigateToPageSlug}
          />
          <EditableContent
            fieldKey="legalLine"
            editable={editable}
            activeEditTarget={target}
            className="ws-footer__legal-line"
          >
            {legalLine}
          </EditableContent>
        </div>
      </div>
    </footer>
  );
}
